import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Heading } from '@mm/components';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { canBeTested } from '@mm/state/helpers/connection-helpers';
import ConnectionModel, {
  ConnectionKind,
} from '@mm/state/model/ConnectionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectWidgetGoogleConnections } from '@mm/state/selectors/connectionsSelectors';
import {
  selectGoogleAccounts,
  selectGoogleAccountsId,
} from '@mm/state/selectors/googleSheetSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { GoogleSheetsIntegrationEditor } from '@mm/stateful-components';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../../common/components/TextInput';
import { useConnectionWidgetHandlers } from '../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { Menu } from '../../../../features/beta/modules/Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './GoogleSheetsIntegrationSlideoutEditor.module.css';

export interface GoogleSheetsIntegrationSlideoutEditorProps {
  widgetId: number;
}

export const GoogleSheetsIntegrationSlideoutEditor = ({
  widgetId,
}: GoogleSheetsIntegrationSlideoutEditorProps) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === widgetId)!;
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const googleConnections = useSelector(selectWidgetGoogleConnections) ?? [];
  const googleSheetsConnections = useSelector(
    (state: StoreState) => state.connections.entities,
  );
  const viewActions = useViewActions();
  const { openSpreadsheetTestConnectionModal, openDeleteConfirmModal } =
    useModalActions();
  const { create, update } = useConnectionCRUDActions();
  const { getSheets, getColumns } = useGoogleSheetsCRUDActions();
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const { retrieveWidgetConnections, deleteConnection, retrieveById } =
    useConnectionCRUDActions();
  const [connection, setConnection] = useState<ConnectionModel | undefined>(
    googleSheetsConnections.find(c => c.id === widgetId),
  );
  const chatbotId = useSelector(activeChatbotIdSelector);
  const [localName, setLocalName] = useState(connection?.name || '');
  const [nameEditMode, setNameEditMode] = useState(false);
  const { setRef } = useSlideoutOnClose();
  const accounts = useSelector(selectGoogleAccounts);
  const accountsId = useSelector(selectGoogleAccountsId);

  useEffect(() => {
    const getConnection = async () => {
      let newConnection;
      if (widget && !widget.config.connectionWebhookId) {
        newConnection = await create(activeFbPageId, {
          name: 'Connection',
          kind: ConnectionKind.WIDGET,
          description: '',
          connectionKind: 'google_spreadsheet',
        });

        if (newConnection) {
          const { sendDataWidgetConfig: config } = newConnection;
          if (config) {
            const clonedWidget = {
              ...widget,
              config: {
                ...config,
                kind: 'google_sheets' as any,
              },
            };
            setConnection(newConnection);
            await connectionWidgetHandlers.update(clonedWidget);
            await retrieveWidgetConnections(
              activeFbPageId,
              'google_spreadsheets',
            );
          }
        }
      } else {
        const connection = widget
          ? googleConnections.find(
              c => c.id === widget.config.connectionWebhookId,
            )
          : googleSheetsConnections.find(c => c.id === widgetId);
        if (connection?.id) {
          newConnection = await retrieveById(
            activeFbPageId,
            Number(connection?.id),
          );
        }
        setConnection(newConnection);
      }
      setLocalName(newConnection?.name || 'Google Sheets Integration');
      const googleAccount = accounts.find(
        acc => acc.id === newConnection?.googleAccountId && !acc.readOnly,
      );
      if (newConnection?.googleAccountId && googleAccount) {
        if (accountsId.includes(newConnection.googleAccountId)) {
          if (newConnection.googleSpreadsheetId) {
            await getSheets(
              newConnection.googleAccountId,
              newConnection.googleSpreadsheetId,
            );
          }
        }
        if (
          newConnection.googleSpreadsheetId &&
          newConnection.googleSheetTitle
        ) {
          await getColumns(
            newConnection.googleAccountId,
            newConnection.googleSpreadsheetId,
            newConnection.googleSheetTitle,
          );
        }
      }
    };

    getConnection();
  }, []);

  const onClose = async () => {
    viewActions.hideSlideoutMenu();
  };

  const updateConnection = async (updatedFields: Partial<ConnectionModel>) => {
    if (!connection) {
      return;
    }
    const updatedConnection = await update(activeFbPageId, {
      ...connection,
      validationMessages: null,
      ...updatedFields,
    });

    if (updatedConnection) {
      setConnection(updatedConnection);
    }
  };

  const onClickActivate = async () => {
    await updateConnection({
      active: true,
    });
  };

  const onStatusChange = () => {
    updateConnection({ active: !connection?.active });
  };

  const onClickTestConnection = () => {
    if (connection) {
      openSpreadsheetTestConnectionModal({ connection, onClickActivate });
    }
  };
  const onClickSaveName = async () => {
    await updateConnection({ name: localName || '' });
    setNameEditMode(false);
    await retrieveWidgetConnections(activeFbPageId, 'google_spreadsheets');
  };

  const onClickCancelSaveName = () => {
    setLocalName(connection?.name || 'Google Sheets Integration');
    setNameEditMode(false);
  };

  const onClickDelete = () => {
    const deleteCallback = async () => {
      if (connection) {
        if (widget) {
          const clonedWidget = {
            ...widget,
            config: {
              connectionWebhookId: null,
              kind: 'google_sheets' as any,
            },
          };
          await connectionWidgetHandlers.update(clonedWidget);
        }
        setTimeout(async () => {
          await deleteConnection(activeFbPageId, connection);
          await retrieveWidgetConnections(
            activeFbPageId,
            'google_spreadsheets',
          );
          viewActions.hideSlideoutMenu();
        }, 400);
      }
    };

    openDeleteConfirmModal({
      headerText: 'Are you sure you want to delete this connection?',
      newStyle: true,
      deleteCallback,
    });
  };
  const disableActions = connection && !canBeTested(connection, true);

  const actionsMenu = [
    {
      disabled: false,
      children: 'Delete',
      onClick: onClickDelete,
    },
  ];

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        {nameEditMode ? (
          <TextInput
            value={localName}
            onChange={e => setLocalName(e.target.value)}
            disabled={!nameEditMode}
            placeholder="Connection Name"
            maxLength={50}
            className={classnames(styles.nameInput, {
              [styles.nameInputDisabledMode]: !nameEditMode,
            })}
          />
        ) : (
          <Heading className={styles.headerTitle} size={3}>
            {connection?.name || 'Google Sheets Integration'}
          </Heading>
        )}

        {!nameEditMode && (
          <EditOutlined
            onClick={() => setNameEditMode(true)}
            className={styles.editIcon}
          />
        )}

        {nameEditMode && (
          <>
            <div onClick={onClickSaveName} className={styles.saveBtn}>
              <CheckOutlined className={styles.checkmark} />
            </div>
            <CloseOutlined
              className={styles.closeIcon}
              onClick={onClickCancelSaveName}
            />
          </>
        )}
        {!nameEditMode && (
          <div className={styles.headerButtons}>
            {connection && (
              <>
                <PrimaryButton
                  color={ButtonColor.secondaryBlue}
                  className={styles.testConnectionBtn}
                  onClick={onClickTestConnection}
                  disabled={disableActions}
                >
                  Test It
                </PrimaryButton>
                <PrimaryButton
                  color={ButtonColor.blue}
                  className={
                    disableActions
                      ? styles.testConnectionBtn
                      : styles.primaryButton
                  }
                  onClick={onStatusChange}
                  disabled={disableActions}
                >
                  {!connection.active ? 'Activate' : 'Pause'}
                </PrimaryButton>
              </>
            )}
            <Menu
              menu={actionsMenu}
              direction="bottom"
              useThreeDotButton
              className={styles.actionMenu}
            />
            <CloseOutlined className={styles.closeIcon} onClick={onClose} />
          </div>
        )}
      </div>

      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.container}>
          <div className={styles.configArea}>
            <GoogleSheetsIntegrationEditor
              connection={connection}
              setConnection={setConnection}
              showConnectionWidgetInfo
            />
          </div>
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
